//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import {
  FAKE_BAZAARVOICE_NETWORK_CONTAINER_ID,
  FAKE_BAZAARVOICE_NETWORK_REEL_ID,
  GALLERIES_ACTIONS,
} from '@galleries/constants/galleriesConstants';
import GalleryContainerId from '@galleries/models/GalleryContainerId';
import {
  contentRightsModalActions,
  ShouldRequireRights,
} from '@galleries/reducers/contentRightsModalSlice';
import { actions as actionBarActions } from '@galleries/actions/galleriesActionBarActions';
import { galleriesDataActions } from '@galleries/reducers/galleriesDataSlice';
import { getIdsOfAlphabetizedGalleries } from '@galleries/selectors/getIdsOfAlphabetizedGalleries';
import getSelectedGallery from '@galleries/selectors/getSelectedGallery';
import { ICuralateStore } from '@models/AppState';
import { getBulkActionsActions } from '@react-bridge/wrappers/ng-service-provider';
import { createAction } from '@utils/actions';
import { ReduxDispatch, ReduxGetState } from 'app/curalate.store';
import { SOURCE_FILTER_VALUES } from '@galleries/models/galleriesStateModel';
import { setSelectedGalleryQueryParam } from '@galleries/utils/setSelectedGalleryQueryParam';
import getGalleriesClientType, {
  GalleriesClientType,
} from '@galleries/selectors/getGalleriesClientType';
import { getValidBazaarvoiceNetworkSources } from '@galleries/selectors/getValidBazaarvoiceNetworkSources';
import { getIsBazaarvoiceNetworkSyndicationFrontendEnabled } from '@uia/rights/selectors/getUIARights';

function selectGalleryById(selectedGalleryId: GalleryContainerId) {
  return createAction({
    type: GALLERIES_ACTIONS.SELECT_GALLERY,
    selectedGalleryId,
  });
}

export type GalleriesPickerActionType = ReturnType<typeof selectGalleryById>;

export const galleryPickerActions = {
  selectGalleryById,

  resetFilterDropdownIfNecessary:
    (selectedGalleryId: GalleryContainerId) =>
    (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const previouslySelectedGallery =
        getState().galleries.picker.selectedGalleryId;
      const previouslySelectedFilteringOption =
        getState().galleries.actionBar.sourceFilterValue;

      const validBazaarvoiceNetworkSources =
        getValidBazaarvoiceNetworkSources(getState());

      const previousGalleryWasBazaarvoiceNetwork =
        previouslySelectedGallery === FAKE_BAZAARVOICE_NETWORK_CONTAINER_ID;
      const newGalleryIsBazaarvoiceNetwork =
        selectedGalleryId === FAKE_BAZAARVOICE_NETWORK_CONTAINER_ID;
      const previousFilteringOptionWasInvalidForBazaarvoiceNetwork =
        !validBazaarvoiceNetworkSources.includes(
          previouslySelectedFilteringOption,
        );
      const previousFilteringOptionWasInvalidForNormalGallery =
        previouslySelectedFilteringOption === SOURCE_FILTER_VALUES.DISTRIBUTED;

      const isSwitchingWithInvalidState =
        (newGalleryIsBazaarvoiceNetwork &&
          !previousGalleryWasBazaarvoiceNetwork &&
          previousFilteringOptionWasInvalidForBazaarvoiceNetwork) ||
        (!newGalleryIsBazaarvoiceNetwork &&
          previousGalleryWasBazaarvoiceNetwork &&
          previousFilteringOptionWasInvalidForNormalGallery);

      // Reset filter dropdown if switching galleries will cause dropdown to be in an invalid state
      if (isSwitchingWithInvalidState) {
        dispatch(
          actionBarActions.updateSourceFilter(SOURCE_FILTER_VALUES.ALL_MEDIA),
        );
      }
    },

  selectGallery:
    (
      selectedGalleryId: GalleryContainerId,
      updateContainerIdParam: boolean = true,
    ) =>
    async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      dispatch(
        galleryPickerActions.resetFilterDropdownIfNecessary(selectedGalleryId),
      );

      const bulkActions = getBulkActionsActions();

      setSelectedGalleryQueryParam(
        updateContainerIdParam ? selectedGalleryId : undefined,
      );

      dispatch(selectGalleryById(selectedGalleryId));
      const gallery = getSelectedGallery(getState());

      if (selectedGalleryId === FAKE_BAZAARVOICE_NETWORK_CONTAINER_ID) {
        dispatch(galleriesDataActions.resetGalleryItems());
        dispatch(galleriesDataActions.getGalleryItems());

        await dispatch(
          galleriesDataActions.requestAutoApprovalConfigurations(
            FAKE_BAZAARVOICE_NETWORK_REEL_ID,
          ),
        );
      } else if (gallery) {
        // side-effects: refetch gallery item data now that the selected gallery has changed
        dispatch(galleriesDataActions.resetGalleryItems());
        dispatch(galleriesDataActions.getGalleryItems());

        // Fire an extra poll to see if we're skipping rights on items. This handles instances when
        //   we've started a skip rights job on one gallery, and then switch back to it.
        dispatch(contentRightsModalActions.pollForJobStatus(gallery.reelId));

        // Set currently-selected rights configuration setting
        dispatch(
          contentRightsModalActions.setShouldRequireRights(
            gallery.isRightsManaged
              ? ShouldRequireRights.requireRights
              : ShouldRequireRights.dontRequireRights,
          ),
        );
        await dispatch(
          galleriesDataActions.requestAutoApprovalConfigurations(
            gallery.reelId,
          ),
        );
      }
    },

  selectDefaultGallery:
    () =>
    async (
      dispatch: ReduxDispatch,
      getState: () => ICuralateStore,
    ): Promise<void> => {
      const alphabetizedGalleryIds = getIdsOfAlphabetizedGalleries(getState());

      const isBazaarvoiceSyndicationEnabed =
        getIsBazaarvoiceNetworkSyndicationFrontendEnabled(getState());
      const galleriesClientType = getGalleriesClientType(getState());

      const isGalleriesEnabled = !!getState().session.features.galleries;
      const isInstashopClient =
        galleriesClientType === GalleriesClientType.instashop;

      const isBazaarvoiceSyndicationOnlyClient =
        isBazaarvoiceSyndicationEnabed &&
        !isGalleriesEnabled &&
        !isInstashopClient;

      const hasNoGalleries = alphabetizedGalleryIds.length === 0;

      // If this is a BV syndication only client or a normal client with 0 galleries,
      // we should select the BV network as the default
      const shouldDefaultToBazaarvoiceNetwork =
        isBazaarvoiceSyndicationEnabed &&
        (isBazaarvoiceSyndicationOnlyClient || hasNoGalleries);

      const defaultGalleryId = shouldDefaultToBazaarvoiceNetwork
        ? FAKE_BAZAARVOICE_NETWORK_CONTAINER_ID
        : alphabetizedGalleryIds[0];

      dispatch(
        galleryPickerActions.selectGallery(
          defaultGalleryId,
          // Don't update the containerId param because this method is just used to reset the galleries page onUnmount
          false,
        ),
      );
    },
};
